import { Check, RotateCcw, Trash2 } from 'lucide-react'
import { useState } from 'react'
import { Card, CardHeader } from '@/components/ui/Card'
import { toDecimal, toNumber } from '@/lib/decimal'
import { formatEuros } from '@/lib/format'
import { useIndexationPreview, useRecurringActions } from '@/lib/recurring-api'
import type { RecurringComment, RecurringDetail, SuggestedPercent } from '@/lib/recurring-types'
import { cn } from '@/lib/utils'

export function AgreementsTab({
  detail,
  isAdmin,
  onLinesRewritten,
}: {
  detail: RecurringDetail
  isAdmin: boolean
  onLinesRewritten: (fresh: RecurringDetail) => void
}) {
  return (
    <div className="space-y-6">
      <CommentsSection id={detail.id} comments={detail.comments} />
      {isAdmin ? <IndexationSection detail={detail} onApplied={onLinesRewritten} /> : null}
    </div>
  )
}

function commentDate(value: string | null): string {
  if (!value) return ''
  return new Date(value).toLocaleDateString('nl-NL', { day: '2-digit', month: 'short', year: 'numeric' })
}

function CommentsSection({ id, comments }: { id: string; comments: RecurringComment[] }) {
  const actions = useRecurringActions(id)
  const [body, setBody] = useState('')

  function submit() {
    const trimmed = body.trim()
    if (!trimmed) return
    actions.addComment.mutate(trimmed)
    setBody('')
  }

  return (
    <Card>
      <CardHeader title="Opmerkingen" subtitle="Open opmerkingen komen als interne notitie op elk nieuw concept. De klant ziet ze niet." />

      {comments.length === 0 ? (
        <p className="mt-4 text-sm text-ink-faint">Nog geen opmerkingen.</p>
      ) : (
        <ul className="mt-4 divide-y divide-hairline">
          {comments.map((comment) => {
            const resolved = comment.resolvedAt !== null
            return (
              <li key={comment.id} className="flex items-start gap-2 py-2">
                <div className={cn('min-w-0 flex-1', resolved && 'opacity-50')}>
                  <p className={cn('text-sm text-ink', resolved && 'line-through')}>{comment.body}</p>
                  <p className="text-xs text-ink-faint">
                    {comment.authorName ?? 'systeem'} · {commentDate(comment.createdAt)}
                    {resolved ? ' · afgehandeld' : ''}
                  </p>
                </div>
                <button
                  type="button"
                  className="btn-ghost h-8 w-8 shrink-0 p-0"
                  aria-label={resolved ? 'Heropenen' : 'Afhandelen'}
                  title={resolved ? 'Heropenen' : 'Afhandelen'}
                  onClick={() => actions.setCommentResolved.mutate({ commentId: comment.id, resolved: !resolved })}
                >
                  {resolved ? <RotateCcw className="h-4 w-4" /> : <Check className="h-4 w-4" />}
                </button>
                <button
                  type="button"
                  className="btn-ghost h-8 w-8 shrink-0 p-0"
                  aria-label="Verwijderen"
                  title="Verwijderen"
                  onClick={() => actions.deleteComment.mutate(comment.id)}
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </li>
            )
          })}
        </ul>
      )}

      <div className="mt-3 flex items-center gap-2">
        <input
          type="text"
          value={body}
          onChange={(e) => setBody(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault()
              submit()
            }
          }}
          placeholder="Plaats een opmerking…"
          className="field"
        />
        <button type="button" className="btn-primary shrink-0" onClick={submit} disabled={!body.trim()}>
          Plaats
        </button>
      </div>
    </Card>
  )
}

function suggestionLabel(suggestion: SuggestedPercent | null): string | null {
  if (!suggestion) return null
  return suggestion.source === 'this_year'
    ? `voorstel: laatst toegepast in ${suggestion.sourceYear}`
    : `voorstel: percentage van ${suggestion.sourceYear}`
}

function IndexationSection({ detail, onApplied }: { detail: RecurringDetail; onApplied: (fresh: RecurringDetail) => void }) {
  const actions = useRecurringActions(detail.id)
  const preview = useIndexationPreview(detail.id)
  const [percent, setPercent] = useState(detail.suggestedPercent ? String(toNumber(detail.suggestedPercent.percent)) : '')

  return (
    <Card>
      <CardHeader
        title="Indexatie"
        subtitle={`Laatst geindexeerd: ${detail.lastIndexationYear ? String(detail.lastIndexationYear) : 'nooit'}${suggestionLabel(detail.suggestedPercent) ? ` · ${suggestionLabel(detail.suggestedPercent)}` : ''}`}
        action={
          detail.indexationPending ? (
            <span className="inline-flex items-center rounded-full bg-status-overdue/10 px-2 py-0.5 text-xs font-medium text-status-overdue">
              Open: generatie geblokkeerd
            </span>
          ) : null
        }
      />
      <div className="mt-4 flex items-center gap-2">
        <input
          className="field num h-9 w-24 text-sm"
          inputMode="decimal"
          value={percent}
          onChange={(e) => setPercent(e.target.value)}
          placeholder="%"
          aria-label="Indexatiepercentage"
        />
        <button
          type="button"
          className="btn-secondary h-9 px-3 text-sm"
          onClick={() => preview.mutate(toDecimal(toNumber(percent.replace(',', '.'))))}
          disabled={!detail.indexed || !percent.trim() || preview.isPending}
        >
          Preview
        </button>
      </div>

      {preview.data ? (
        <div className="mt-4 rounded-lg border border-accent-500/30 bg-accent-600/4 p-3">
          <p className="mb-2 text-sm font-medium text-ink">
            Indexatie +{toNumber(preview.data.percent)}% ({preview.data.effectiveYear})
          </p>
          <div className="space-y-1.5">
            {preview.data.lines.map((line) => (
              <div key={line.description} className="flex items-center justify-between text-sm">
                <span className="text-ink-soft">{line.description}</span>
                <span className="money text-ink-faint">
                  {formatEuros(toNumber(line.oldAmount))} <span className="text-accent-700">-&gt; {formatEuros(toNumber(line.newAmount))}</span>
                </span>
              </div>
            ))}
            <div className="flex items-center justify-between border-t border-hairline pt-1.5 text-sm font-semibold">
              <span className="text-ink">Totaal</span>
              <span className="money text-ink">
                {formatEuros(toNumber(preview.data.oldTotal))} -&gt; {formatEuros(toNumber(preview.data.newTotal))}
              </span>
            </div>
          </div>
          <button
            type="button"
            className="btn-primary mt-3 h-8 w-full text-sm"
            onClick={() =>
              preview.data &&
              actions.indexationApply.mutate(toDecimal(toNumber(preview.data.percent)), { onSuccess: (fresh) => onApplied(fresh) })
            }
            disabled={actions.indexationApply.isPending}
          >
            Indexatie toepassen
          </button>
        </div>
      ) : null}
    </Card>
  )
}
